import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo11Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 11 BWV 782
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV782/bach-invention-11/bach-invention-11-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 11 BWV 782.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4667">
      <Navbar data-id="element-4668" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4669">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4670">
          <img src="https://images.unsplash.com/photo-1552422535-c45813c61732?w=400&h=300&fit=crop" alt="Invention No. 11" className="w-full h-64 md:h-80 object-cover" data-id="element-4671" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4672" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4673">
            <div className="max-w-7xl mx-auto" data-id="element-4674">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4675">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4676" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4677">
                Invention No. 11 in G minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4678">BWV 782</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4679">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4680">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4681">
          <div className="flex items-center space-x-2" data-id="element-4682">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4683">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4684" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4685">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4686">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4687" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4688">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4689">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4690" />
              <span className="hidden sm:inline" data-id="element-4691">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4692">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4693" />
              <span className="hidden sm:inline" data-id="element-4694">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4695">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4696">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4697">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 11 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4698" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4699">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4700">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4701">
            <div data-id="element-4702">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4703">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4704">
                <div data-id="element-4705">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4706">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4707">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4708">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4709">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4710">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4711">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4712">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4713">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4714">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4715">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4716">
                    G minor
                  </dd>
                </div>
                <div data-id="element-4717">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4718">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4719">
                    BWV 782
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4720">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4721">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4722">
                The eleventh of Bach's fifteen Two-Part Inventions, this piece
                in G minor is notable for its expressive character and elegant
                contrapuntal writing. Written as pedagogical works for his
                eldest son Wilhelm Friedemann, the Inventions demonstrate how to
                play cleanly in two voices and develop musical ideas
                systematically. This invention features a lyrical subject that
                moves gracefully between the hands, creating an intimate
                dialogue. The minor key gives the piece a contemplative,
                melancholic quality, while the flowing sixteenth-note passages
                require smooth legato playing and careful voice leading. The
                work exemplifies Bach's ability to combine technical instruction
                with genuine musical expression, making it both an essential
                study piece and a deeply satisfying performance work.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4723" />
    </div>;
};